import React, { useState } from 'react';
import { Box, Button, Typography, IconButton, Chip } from '@mui/material';
import { Plus, Trash2, X } from 'lucide-react';
import { Reorder } from '@mui/icons-material';
import ReorderPantryDialog from '../dialogs/ReorderPantryDialog';
import { PantryItem } from '../../api';
import './PantryHeader.scss';

interface PantryHeaderProps {
  onUploadClick: () => void;
  selectedCount?: number;
  onBulkDelete?: () => void;
  onClearSelection?: () => void;
  pantryItems: PantryItem[];
  onReorderComplete?: () => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const PantryHeader: React.FC<PantryHeaderProps> = ({
  onUploadClick,
  selectedCount = 0,
  onBulkDelete,
  onClearSelection,
  pantryItems,
  onReorderComplete,
  hasEditAccess = true,
  hasManageAccess = true,
}) => {
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);

  return (
    <Box className="pantry-header">
      <Box className="pantry-header-content">
        <Typography variant="h5" component="h1" className="pantry-title">
          Pantry
        </Typography>
        {selectedCount > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={`${selectedCount} item${selectedCount !== 1 ? 's' : ''} selected`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={onClearSelection}
              deleteIcon={<X size={16} />}
              sx={{
                '& .MuiChip-deleteIcon': {
                  color: '#6b7280',
                  '&:hover': { color: '#374151' }
                }
              }}
            />
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Manage your pantry files and documents
          </Typography>
        )}
      </Box>

      <Box className="pantry-header-actions">
        {selectedCount > 0 && hasManageAccess ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<Trash2 className="h-4 w-4" />}
              onClick={onBulkDelete}
              sx={{
                backgroundColor: '#dc2626',
                '&:hover': {
                  backgroundColor: '#b91c1c',
                }
              }}
            >
              Delete Selected ({selectedCount})
            </Button>
          </Box>
        ) : hasEditAccess ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={onUploadClick}
              className="create-button"
            >
              Add Pantry
            </Button>
            <Button
              variant="outlined"
              startIcon={<Reorder />}
              onClick={() => setReorderDialogOpen(true)}
              sx={{
                color: 'hsl(var(--primary))',
                borderColor: 'hsl(var(--primary))',
                '&:hover': {
                  borderColor: 'hsl(var(--primary))',
                  backgroundColor: 'rgba(22, 48, 109, 0.04)'
                }
              }}
            >
              Reorder Pantry
            </Button>
          </Box>
        ) : null}
      </Box>

      <ReorderPantryDialog
        open={reorderDialogOpen}
        onClose={() => setReorderDialogOpen(false)}
        pantryItems={pantryItems}
        onReorderComplete={onReorderComplete}
      />
    </Box>
  );
};

export default PantryHeader;
